// Desktop app Specific functionality (nto required for mobile)
import * as angular from 'angular';

import '@common/navkendo/navitemsgrid';
import './navkendo/navmenu';

import * as rwf3 from '@common/rwf';
import rwf3pages = rwf3.Pages;
import { RWFRootComponent } from './rwfroot/RWFRoot.component';
import { RwfInvoicesGrid } from './rwf/rwfinvoicesgrid';
import { KWFPreviewService } from './rwfpreview/KWFPreviewService';
import { RwfUserInteraction } from './rwfuserinteraction/rwf.userinteraction';
import { RoutingService } from './services/routingservice';
import { KWFAppActions } from './KWFAppActions';
import { DialogService } from './services/dialogservice';
import { CompatibilityService } from '@common/services/compatibilityservice';
import { NavClientService } from '@common/navdata';
import {
    CreateTemplate, RenderAsLinkProps, GetAmountTemplate, template as ktemplate,
    constants as kconstants, GetDurationTemplate, GetOverDueTemplate
} from '@common/navkendo';
import { IRwfBootstrapParameters, RwfInvoiceDataService, FormatIBAN, UnFormatIBAN } from '@common/rwf';
import { FDRW_Workflow_Running_Instances } from '@common/rwf/rwf.const';

import { fn, IInfOperation, FixupRelPath } from '@common/utils';
import { GetCustomKendoTemplate } from './kendo/templates';
import { CtrlChangeNavUserPassword } from './rwfuserinteraction/changepassword/changepassword.ctrl';
import { IKWFRoute, IKWFRootScope, IWKFRouteAccess } from './KWFMain';
import { KWFApplicationService } from './KWFApplication';
import { NavSettingsService } from '@common/services/settingsservice';
import { ITileSettings } from './infotiles/infotiles.ctrl';
import { $locationChangeCancel } from '@common/rwf/rwfedit.common';
import { IsExternalControlled } from './rwf.spa.proxy';
import { RWFPreviewController } from './rwfpreview/rwfpreviewcontroller';

//#region Constants

/** Default Aplication Window Title, May be Customized by IKWFRoute.TitleFormula */
var Default_Product = "Workflow";
var DEFAULT_ApplicationTitle = `Axians Infoma Workflow`;
/** Default Application MainBar Title (deferred from $route.name) */
const DEFAULT_Title = "";

//#endregion

/** Setup Progress/Error Notification during a Route Change */
function SetupRouteChangeNotification($rootScope: IKWFRootScope, $location: ng.ILocationService, DialogService: DialogService) {
    var body = $("#mainView");
    var notify = (start: boolean) => {
        body.kendoNavBusyIndicator(start);
        $rootScope["IsInRefresh"] = start;
    };
    $rootScope.$on($locationChangeCancel, notify.bind(this, false));
    $rootScope.$on("$routeChangeError", (p, c, e, error) => {
        DialogService.ShowError(error || "Das Wechseln der Ansicht ist fehlgeschlagen").then(() => {
            var navHistory = $rootScope.NavigationHistory;
            var finalFallback = $location.search()["source"];
            var fallbackUrl = navHistory && navHistory.length ? navHistory[navHistory.length - 1] : finalFallback;
            if (fallbackUrl) {
                var isabsolute = (fallbackUrl.indexOf("http:") == 0 || fallbackUrl.indexOf("https:") == 0);
                if (isabsolute) {
                    window.setTimeout(() => window.location.replace(fallbackUrl));
                } else {
                    $location.url(fallbackUrl);
                }
            }
        }).finally(() => notify(false));
    });
    $rootScope.$on("$routeChangeStart", notify.bind(this, true));
    $rootScope.$on("$routeChangeSuccess", notify.bind(this, false));
}

/** Initial Run function of Application
 * - Show RouteChange Errors
 * - Handle Setting/Watching of CurrentTitle/CurrentDescription on rootScope
 */
var kwfInit = ["DialogService", "CompatibilityService", "$rootScope", "$route", "$location", "KWFApplication", "NavSettings", "TileSettings", function (
    DialogService: DialogService,
    $c: CompatibilityService,
    $rootScope: IKWFRootScope,
    $routeService: ng.route.IRouteService,
    $location: ng.ILocationService,
    KWFApplication: KWFApplicationService,
    NavSettings: NavSettingsService,
    TileSettings: ITileSettings
) {
    SetupRouteChangeNotification($rootScope, $location, DialogService);
    $rootScope.ApplicationTitle = DEFAULT_ApplicationTitle;
    $rootScope.ApplicationProduct = Default_Product;
    $rootScope.ApplicationState = KWFApplication.ApplicationState;
    KWFApplication.GetGlobalSettings().then(settings => {
        $rootScope.KWFSettings = settings;
        KWFApplication.RwfAppSettings.Get().then(appSettings => {
            $rootScope.KWFApplicationSettings = appSettings;
            // Overwrite Tile Settings Constant values from KWF Tile Settings
            $.extend(TileSettings, appSettings && appSettings.TileSettings);
        });
    }, err => {
        // Try to Assign Settings in Error case
        $rootScope.KWFSettings = KWFApplication.LoadedSettings;
    });

    var appActions = $rootScope.AppActions || ($rootScope.AppActions = {});
    // Setup Reset Global User Settings Action
    var actResetAllUserSettings = appActions["ResetAllUserSettings"] = {
        Hidden: "Enabled", Enabled: true,
        Execute: () => DialogService.WhenConfirm("Sollen wirklich ALLE benutzerspezifischen Einstellungen\n (Spaltenkonfigurationen, gespeicherte Suchen, ...) für alle Ansichten zurückgesetzt werden?\n"
            + "Die Seite wird dann neu geladen!").then(() =>
                NavSettings.DeleteAllSettings().then(actReload.Execute))
    };

    const actOpenInfomaClient = appActions["OpenInfomaClient"] = {
        Hidden: "Enabled", Enabled: <boolean>false,
        Execute: () => { window.open(fn.GetAbsoluteUrlPrefix(true) + '/InfomaSmartClient/Infoma.SmartClient.application?appType=RWF' , '_blank'); }
    };
    $c.resolve("Rwf")("RwfBootstrap").Resolve.then(rwfBootstrapParameters => {
        actOpenInfomaClient.Enabled = !rwfBootstrapParameters.InfomaClient && (rwfBootstrapParameters.RwfSettings.InfomaSmartClientAvailable == true);
    });

    // Setup Reload Current Page Action
    var actReload = appActions["ForceReloadPage"] = {
        Enabled: true,
        Execute: () => {
            window["_NavReload"] = true;
            window.location.reload(true);
            window.setTimeout(() => { delete window["_NavReload"]; }, 2000);
        }
    };
    // SetUp "Home" Link Action
    var actHomeAction = appActions["Home"] = <IInfOperation & { Url: string }>{
        Enabled: true,
        Url: FixupRelPath("#/"),
        Execute: () => { $location.url(actHomeAction.Url); }
    };
    /** Explicitly Settings "nav", "ssp", "company" keep them when opening new window */
    function refreshHome() {
        var s = $location.search();
        var p = fn.EncodeQueryData({ nav: s["nav"], ssp: s["ssp"], company: s["company"] });
        actHomeAction.Url = FixupRelPath("#/" + (p ? "?" + p : ""));
    }
    $rootScope.$on("$routeChangeSuccess", refreshHome);
    $rootScope.ShowBar = true;
    $rootScope.AllowChangeCompany = true;
    registerOnRouteChanged($rootScope.$watch<string>("::KWFSettings.CurrentCompany", (newVal) => $rootScope.CurrentDescription = newVal));
    $rootScope.CurrentTitle = DEFAULT_Title;

    $rootScope.Navigations = <any>Object.getOwnPropertyNames($routeService.routes).map(item => {
        var kwfRoute = <IWKFRouteAccess>$routeService.routes[item];

        if (kwfRoute.keys && kwfRoute.keys.length > 0)
            return;
        if (!kwfRoute.name || !kwfRoute.originalPath || kwfRoute.originalPath.lastIndexOf("/") == kwfRoute.originalPath.length + 1)
            return;
        if (kwfRoute.originalPath == "/")
            return;

        return {
            name: kwfRoute.name,
            url: kwfRoute.originalPath
        };
    }).filter(item => !!item);

    /** Reset Root State Watchers */
    function fireRouteChanged() {
        $rootScope.$emit("$kwfResetRootWatcher");
    }
    /** Register Root State Watcher (implicit Reset on current Route Change!) */
    function registerOnRouteChanged(func: Function) {
        var reset = $rootScope.$on("$kwfResetRootWatcher", () => { reset(); func(); });
    };

    $rootScope.$on("$routeChangeError", fireRouteChanged);

    // Refresh Description Formulas After a New View Is Loaded
    $rootScope.$on("$viewContentLoaded", () => {
        var route: IKWFRoute = <any>($routeService.current && $routeService.current["$$route"]);
        var scope: ng.IScope = $routeService.current && $routeService.current.locals.$scope;

        if (route && route.NameFormula) {
            const deregNameFormula = scope.$watch<string>(<any>route.NameFormula, (newValue => {
                $rootScope.CurrentTitle = newValue || (route && route.name) || DEFAULT_Title;
            }));
            const tearDownNameFormula = $rootScope.$on("$viewContentLoaded", () => {
                deregNameFormula();
                tearDownNameFormula();
            });
        } else {
            $rootScope.CurrentTitle = (route && route.name) || DEFAULT_Title;
        }
        fireRouteChanged();
        const isExternalControlled = IsExternalControlled();
        if (scope && route) {
            registerOnRouteChanged(scope.$watch<string>(route.DescriptionFormula || "::KWFSettings.CurrentCompany", (newVal) => $rootScope.CurrentDescription = newVal));
            registerOnRouteChanged(scope.$watch<string>(route.TitleFormula, (newVal) => $rootScope.ApplicationTitle = (newVal || DEFAULT_ApplicationTitle)));

            $rootScope.ShowBar = !isExternalControlled && !route.HideBar;
            $rootScope.AllowChangeCompany = !isExternalControlled && !!route.AllowChangeCompany;
        } else {
            $rootScope.ShowBar = !isExternalControlled;
            $rootScope.AllowChangeCompany = !isExternalControlled;
        }
    });

    $rootScope.$on('$routeChangeSuccess', () => ($rootScope.NavigationHistory || ($rootScope.NavigationHistory = [])).push($location["$$path"]));
}];

angular.module("RWF.Desktop.Services", ["kendo.directives", "KWFApplication", "infoma.editmarkup"])
.directive("rwfroot", RWFRootComponent.Directive)
.directive("kwfinvoicedatagrid", RwfInvoicesGrid)
.directive('rwfpreview', RWFPreviewController.Directive)
.service("RwfUserInteraction", RwfUserInteraction)
.service("DialogService", DialogService)
.service("Routing", RoutingService)
.service("KWFAppActions", KWFAppActions)
.service("KWFPreviewService", KWFPreviewService);

angular.module("RWF.Desktop", ["RWF.Desktop.Services", "KWF.Sign"])
    .run(kwfInit)
    .run(CtrlChangeNavUserPassword.Init)
    .run(["CompatibilityService", "NavClient", (
        $c: CompatibilityService,
        NavClient: NavClientService
    ) => {
        const $rwf = $c.resolve("Rwf");

        const TEMPLATE_IBAN_TRUNC = CreateTemplate(
            `# var _val_ = FormatIBAN(_VARNAME_) #<div class="idms_truncate" title = "#= _val_ #" >#= _val_ #</div>`,
            { FormatIBAN: FormatIBAN });
        const TEMPLATE_IBAN_NOWRAP = CreateTemplate(
            `# var _val_ = FormatIBAN(_VARNAME_) #<div class="idms_NoWrap" title = "#= _val_ #" >#= _val_ #</div>`,
            { FormatIBAN: FormatIBAN });

        const customIBANTransform = {
            CustomColumn: col => {
                var varName = ("data." + (col.Prefix || "")) + col.Property.Name;
                col.Column.template = TEMPLATE_IBAN_TRUNC(varName);
            },
            CustomDDColumn: c => c.Template = TEMPLATE_IBAN_NOWRAP((c.Prefix ? c.Prefix + "." : "") + c.Property.Name),
            CustomConstraint: c => c.Constraint.Value = UnFormatIBAN(c.Constraint.Value),
            PrepareClient: e => e.value = FormatIBAN(e.value),
            RevertServer: e => e.value = ("" + e.value).split(' ').join('')
        };
        NavClient.RegisterValueTransform({ Property: "IBAN" }, customIBANTransform);
        NavClient.RegisterValueTransform({ Property: "IBANToApply" }, customIBANTransform);

        const TEMPLATE_LINK = CreateTemplate(
            `#= IsValidLink(_VARNAME_) ? '<a class="idms_truncate" title="' + _VARNAME_ + '" target="_blank" href="' + _VARNAME_ +'">' + _VARNAME_ + '</a>' : _VARNAME_#`,
            { IsValidLink: fn.IsValidLink });

        // Register Custom User Columns Config
        // Must be done "early" : NavUrl - Resolution is too late unforunaltely ...
        const RwfAppSettings = $rwf("RwfAppSettings");
        const RwfBootstrap = $rwf("RwfBootstrap");
        RwfAppSettings.Get().then(settings => {
            RwfBootstrap.OnResolved((b: IRwfBootstrapParameters) => {
                const navUrl = b.NavUrlInfo.FullUrl;
                // MSQQQ : used in templates!
                window["RwfInvoiceDataService"] = RwfInvoiceDataService;
                var dummy: (userId: string, navUrl: string) => string = RwfInvoiceDataService.ResolveUser; // Caution used as string here
                var funcName = "RwfInvoiceDataService.ResolveUser",
                    validated = kendo.getter(funcName + ".constructor")(window);

                var userColumns = settings.UserColumns || [];

                userColumns.forEach(colInfo => {
                    $.each($.isArray(colInfo.Page) ? colInfo.Page : [colInfo.Page], (idx, pageName: string) => {
                        $.each(colInfo.Fields, (_idx, fieldName: string) => {
                            NavClient.RegisterCustomColumn({ PageName: pageName, Property: fieldName }, colInfo => {
                                let col = colInfo.Column,
                                    prop = colInfo.Property,
                                    fullField = "data." + (colInfo.Prefix || "") + prop.Name;
                                col.template = ktemplate("<div  class='idms_truncate' title='#:" + fullField + "#'>#:" + funcName + "(" + fullField + ",'" + navUrl + "') #</div>");
                                col.groupHeaderTemplate = ktemplate(prop.Title + ":  #:" + funcName + "(data.value,'" + navUrl + "') #");

                                // Transform User Columns to 'Enum Typed' Col with all Available Users
                                var users = RwfInvoiceDataService.GetUsers(navUrl);
                                if (users && users.length > 0) {
                                    var enums = prop.EnumMappings = users.map(user => {
                                        let technicalName = user.ID,
                                            displayName = user.UserName,
                                            both = technicalName && displayName && displayName != technicalName,
                                            friendlyUserName = both ? displayName + " (" + technicalName + ")" : (technicalName || displayName);
                                        return {
                                            Title: friendlyUserName,
                                            Value: <any>user.ID,
                                            Enum: user.ID,
                                        };
                                    });
                                    prop.IsUsers = true;
                                    col.values = enums.map(val => { return { text: val.Title, value: val.Value }; });
                                }
                            });
                        });
                    });
                });
            });
        });

        const renderAsLinkProps = RenderAsLinkProps;
        RwfBootstrap.OnResolved(() => {
            const invoiceData = $rwf("RwfInvoiceData");
            fn.UISafe(function AjdustInvoiceTemplates() {
                const wfMyInstances = rwf3pages.FDRW_Workflow_User_Instances_List,
                    wfFields = wfMyInstances.Fields,
                    wfAllInstances = rwf3pages.FDRW_Workflow_Invoice_Ledger;
                // User Instances, AllInstances : Globally Register Custom Templated Columns
                [wfMyInstances.PageName, wfAllInstances.PageName, FDRW_Workflow_Running_Instances].forEach(pageName => {

                    // Prepare INvoice Data for Custom Columns Display
                    NavClient.RegisterReadMultiple({ PageName: pageName }, data => {
                        invoiceData.PrepareInvoiceData(data.Data);
                    });

                    NavClient.RegisterCustomColumn({ PageName: pageName, Property: wfFields.Amount }, col => {
                        col.Column.template = GetAmountTemplate(wfFields.Amount, col.Column.format, "", "", col.Prefix);
                    });

                    renderAsLinkProps.forEach(p => {
                        NavClient.RegisterCustomColumn({ PageName: pageName, Property: p }, col => {
                            var fName = ("data." + (col.Prefix || "")) + col.Property.Name;
                            col.Column.template = TEMPLATE_LINK(fName);
                        });
                    });

                    // Erstellt (+ Bearbeitungszeit)
                    NavClient.RegisterCustomColumn({ PageName: pageName, Property: wfFields.Created }, col => {
                        const showDuration = true;
                        col.Column.template = GetDurationTemplate(wfFields.Created, showDuration, col.Column.format, col.Prefix);
                        col.Column.width = showDuration ? "220px" : "168px";
                    });

                    // Adapt DueDate column (append OverDue + highlight)
                    NavClient.RegisterCustomColumn({ PageName: pageName, Property: wfFields.Due_Date }, col => {
                        col.Column.template = GetOverDueTemplate(wfFields.Due_Date, col.Column.format, col.Prefix);
                        col.Column.width = "138px"; //"110px"
                    });

                    // Template CurrentBreakout column with icon
                    NavClient.RegisterCustomColumn({ PageName: pageName, Property: wfFields.CurrentBreakout }, col => {
                        if (!col.Prefix) { // Auf diese Art z. B. nicht im MassenWeiterleitungsfenster ....
                            col.Column.template = GetCustomKendoTemplate("DataGridCurrentBreakoutContentTemplate");
                            col.Column.width = kconstants.KendoGrid_ActionIconColumnSize;
                            col.Column.filterable = false;
                        }
                    });
                });
            });
        });
    }])
    ;
